import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { SplitSection } from '../components/layout/SplitSection';
import { FindingExample } from '../components/pillars/FindingExample';
import { ResearchStatusNote } from '../components/pillars/ResearchStatusNote';
import { PillarId, getPillar, pillarHref, pillars } from '../data/pillars';
import { fadeUp } from '../utils/motion';
type PillarPageProps = {
  id: PillarId;
};
export function PillarPage({
  id
}: PillarPageProps) {
  const pillar = getPillar(id);
  const previous = pillars.find(item => item.order === pillar.order - 1);
  const next = pillars.find(item => item.order === pillar.order + 1);
  return <>
      <PageHero eyebrow={`NCJI · Pillar ${pillar.order} of ${pillars.length}`} title={pillar.name} lead={pillar.description} backLink={{
      href: '/ncji#pillars',
      label: 'NCJI’s five pillars'
    }} data-id="element-3112" data-mp="oue9jria6dww">
        <p className="mt-8 border-l-4 border-teal pl-6 font-heading font-semibold text-xl text-ink-dark leading-relaxed" data-id="element-3113" data-mp="2j7gscas4qcou">
          “{pillar.guidingQuestion}”
        </p>
      </PageHero>

      <SplitSection tone="muted" title="What this pillar covers" intro={pillar.overview} data-id="element-3114" data-mp="2aedcxwxxbprk">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 border-t border-line pt-10" data-id="element-3115" data-mp="25g28k1qee7gq">
          <motion.div {...fadeUp} data-id="element-3116" data-mp="20h8r5y7zpwba">
            <h3 className="font-heading font-bold text-xl text-ink-dark mb-4" data-id="element-3117" data-mp="20iaf33a3udaf">Subdomains</h3>
            <ul className="space-y-3" data-id="element-3118" data-mp="1vguutdfr4at3">
              {pillar.subdomains.map(item => <li key={item} className="flex gap-3 text-lg text-ink-dark leading-relaxed" data-id="element-3119" data-mp="1rh2gek3ofaft">
                  <span aria-hidden="true" className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-teal" data-id="element-3120" data-mp="1mhznfy0z0uo7" />
                  {item}
                </li>)}
            </ul>
          </motion.div>
          <motion.div {...fadeUp} data-id="element-3121" data-mp="3gw0mok0fnjot">
            <h3 className="font-heading font-bold text-xl text-ink-dark mb-4" data-id="element-3122" data-mp="3gsw7yspcd5jw">Related development activities</h3>
            <ul className="space-y-3" data-id="element-3123" data-mp="3sd8vlhh7ozt">
              {pillar.developmentActivities.map(item => <li key={item} className="flex gap-3 text-lg text-ink-dark leading-relaxed" data-id="element-3124" data-mp="8mr6lujm287n">
                  <span aria-hidden="true" className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-navy" data-id="element-3125" data-mp="3qx2xfxdv6dhh" />
                  {item}
                </li>)}
            </ul>
          </motion.div>
        </div>
      </SplitSection>

      <SplitSection title="How a finding is presented" intro={['Each finding connects a specific task to the response behind it, the reasoning observed, the rubric applied and the limits of what the evidence shows.', 'Findings are reviewed by people and linked to a development activity. Service or access decisions are never made from an assessment finding alone.']} data-id="element-3126" data-mp="rk271igzb7oo">
        <FindingExample pillarName={pillar.name} finding={pillar.exampleFinding} data-id="element-3127" data-mp="33a4i531hdbl5" />
      </SplitSection>

      <section className="bg-surface-bg border-t border-line py-14 lg:py-16" data-id="element-3128" data-mp="2vo917a632xdv">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3129" data-mp="2qpo1dbuqufpy">
          <ResearchStatusNote className="max-w-3xl" data-id="element-3130" data-mp="34qieg7sql4uy" />
          <nav aria-label="Other pillars" className="mt-12 pt-8 border-t border-line flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6" data-id="element-3131" data-mp="34pyu6kfm64dr">
            {previous ? <a href={pillarHref(previous.id)} className="group inline-flex items-center gap-2 font-semibold text-navy hover:text-navy-light transition-colors duration-150" data-id="element-3132" data-mp="2bzqjterbko9q">
                <ArrowLeft size={16} aria-hidden="true" data-id="element-3133" data-mp="2lt7d8hnjo1cv" />
                <span data-id="element-3134" data-mp="2h161qxy7aenw">
                  <span className="block text-xs font-medium text-ink-muted" data-id="element-3135" data-mp="2gzv0b4s89izy">Previous pillar</span>
                  {previous.name}
                </span>
              </a> : <span data-id="element-3136" data-mp="1od0hsdez0tp4" />}
            <a href="/ncji" className="text-sm font-medium text-ink-muted hover:text-navy transition-colors duration-150" data-id="element-3137" data-mp="1obyv1boleirk">
              Back to NCJI
            </a>
            {next ? <a href={pillarHref(next.id)} className="group inline-flex items-center gap-2 font-semibold text-navy hover:text-navy-light transition-colors duration-150 sm:text-right" data-id="element-3138" data-mp="26yk9gyk3cjy7">
                <span data-id="element-3139" data-mp="2gox11aqbpddu">
                  <span className="block text-xs font-medium text-ink-muted" data-id="element-3140" data-mp="2gp6l6hoosqbm">Next pillar</span>
                  {next.name}
                </span>
                <ArrowRight size={16} aria-hidden="true" data-id="element-3141" data-mp="2po0nmoop8ax3" />
              </a> : <span data-id="element-3142" data-mp="2unwinyff4s0i" />}
          </nav>
        </div>
      </section>
    </>;
}